import React, { useState, useEffect } from "react";
import CustomButton from "../../../../ui/CommonButton";
import { ReactComponent as ArrowUp } from "../../../../../assets/icons/svg/arrow-up.svg";
import { ReactComponent as ArrowDown } from "../../../../../assets/icons/svg/arrow-down.svg";
import { ReactComponent as CloseIcon } from "../../../../../assets/icons/svg/cancel-icon.svg";

interface FilterDrawerContentProps {
  selectedTags: string[];
  setSelectedTags: React.Dispatch<React.SetStateAction<string[]>>;
  onClearAll: () => void;
  onApply: () => void;
  onCancel: () => void;
  categories: {
    [category: string]: string[];
  };
}

const FilterDrawerContent: React.FC<FilterDrawerContentProps> = ({
  selectedTags,
  setSelectedTags,
  onClearAll,
  onApply,
  onCancel,
  categories,
}) => {
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [categorySelections, setCategorySelections] = useState<{
    [category: string]: string[];
  }>({});

  // Handle checkbox toggle
  const handleToggle = (category: string, tag: string) => {
    const currentSelection = categorySelections[category] || [];

    const updatedCategorySelection = currentSelection.includes(tag)
      ? currentSelection.filter((t) => t !== tag)
      : [...currentSelection, tag];

    const updatedSelections = {
      ...categorySelections,
      [category]: updatedCategorySelection,
    };

    setCategorySelections(updatedSelections);

    // Flatten and de-duplicate all selected tags
    const allSelected = Object.values(updatedSelections).flat();
    const uniqueSelected = Array.from(new Set(allSelected));
    setSelectedTags(uniqueSelected);
  };

  // Compute Comprizon (common) tags
  const getComprizonTags = (): string[] => {
    const selections = Object.values(categorySelections).filter(
      (arr) => arr.length > 0
    );
    if (selections.length < 2) return [];

    return selections.reduce((acc, curr) =>
      acc.filter((tag) => curr.includes(tag))
    );
  };

  const comprizonTags = getComprizonTags();

  const toggleDropdown = (category: string) => {
    setOpenCategory((prev) => (prev === category ? null : category));
  };

  return (
    <div className="flex flex-col h-full p-1">
      {/* Selected Tags */}
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm font-medium text-gray-700">
          Selected ({selectedTags.length})
        </p>
        {selectedTags.length > 0 && (
          <button
            onClick={() => {
              setCategorySelections({});
              onClearAll();
            }}
            className="text-sm text-red-500 underline hover:text-red-600"
          >
            Clear all
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-[6px] mb-6">
        {selectedTags.map((tag) => (
          <div
            key={tag}
            className="flex items-center px-2 py-1 text-sm bg-[#F8F8F8] rounded-[10px] border-[0.5px] border-[#808191]"
          >
            {tag}
            <button
              onClick={() => {
                const updatedSelections = { ...categorySelections };
                Object.keys(updatedSelections).forEach((cat) => {
                  updatedSelections[cat] = updatedSelections[cat].filter(
                    (t) => t !== tag
                  );
                });
                setCategorySelections(updatedSelections);
                setSelectedTags(selectedTags.filter((t) => t !== tag));
              }}
              className="ml-2 text-gray-600 hover:text-red-600"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
        ))}
      </div>

      <hr className="border-t border-gray-200 mb-6" />

      {/* Filter Categories */}
      <div className="space-y-4 overflow-auto flex-1">
        {Object.entries(categories).map(([category, options]) => (
          <div
            key={category}
            className="rounded-[10px] p-2 2xl:p-3 bg-[#F8F8F8] "
          >
            <button
              onClick={() => toggleDropdown(category)}
              className="w-full flex justify-between items-center text-left text-sm 2xl:text-base font-medium px-3"
            >
              {category}
              <span>
                {openCategory === category ? <ArrowUp /> : <ArrowDown />}
              </span>
            </button>

            {openCategory === category && (
              <div className="mt-3 space-y-3 px-3">
                {options.map((option) => (
                  <label
                    key={option}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={(categorySelections[category] || []).includes(
                        option
                      )}
                      onChange={() => handleToggle(category, option)}
                      className="accent-[#0BA6C7]"
                    />
                    <span className="text-sm">{option}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Comprizon Filter Display */}
      {comprizonTags.length >= 1 && (
        <div className="bg-blue-50 border border-blue-200 p-2 rounded-md mt-3 text-sm">
          <p className="text-blue-700 font-medium mb-1">Comprizon Tags</p>
          <div className="flex flex-wrap gap-2">
            {comprizonTags.map((tag) => (
              <span
                key={tag}
                className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md border border-blue-300 text-xs"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Footer Buttons */}
      <div className="flex justify-between gap-3 pt-4 border-t mt-4">
        <CustomButton variant="outline" onClick={onCancel} className="flex-1">
          Cancel
        </CustomButton>

        <CustomButton
          onClick={onApply}
          isActive={selectedTags.length > 0}
          disabled={selectedTags.length === 0}
          className="flex-1 disabled:cursor-not-allowed"
        >
          Apply changes
        </CustomButton>
      </div>
    </div>
  );
};

export default FilterDrawerContent;
